import React, { useState, useEffect } from 'react';
import {
  Search, FileText, AlertTriangle, CheckCircle, Clock,
  Download, Eye, RefreshCw, ChevronDown,
} from 'lucide-react';
import useEmployeeDocuments from '../../../hooks/useEmployeeDocuments';
import { DocumentStatusBadge, CategoryIcon, DocumentPreview } from './components';
import DocumentDetails from './DocumentDetails';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 15;

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024)          return bytes + ' B';
  if (bytes < 1024 * 1024)   return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function DocumentsList({ employeeId = null }) {
  const { loadDocuments, loadEmployeeDocuments, loadCategorias, downloadDocument, loading } = useEmployeeDocuments();

  const [documents,        setDocuments]        = useState([]);
  const [allDocuments,     setAllDocuments]     = useState([]);
  const [categorias,       setCategorias]       = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewDocument,  setPreviewDocument]  = useState(null);
  const [search,           setSearch]           = useState('');
  const [filterCategoria,  setFilterCategoria]  = useState('');
  const [filterEstado,     setFilterEstado]     = useState('');
  const [stats,            setStats]            = useState({ total: 0, approved: 0, pending: 0, vencidos: 0 });
  const [page,             setPage]             = useState(1);

  useEffect(() => {
    loadData();
    loadCategoriasData();
  }, [employeeId]); // eslint-disable-line

  // Filtro local (búsqueda + categoría + estado)
  useEffect(() => {
    let filtered = allDocuments;

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.documento_nombre?.toLowerCase().includes(q) ||
        d.tipo_documento_nombre?.toLowerCase().includes(q) ||
        d.empleado_nombre?.toLowerCase().includes(q) ||
        d.numero_documento?.toLowerCase().includes(q)
      );
    }

    if (filterCategoria) filtered = filtered.filter(d => d.categoria_codigo === filterCategoria);
    if (filterEstado)    filtered = filtered.filter(d => d.estado === filterEstado);

    setDocuments(filtered);
    calcStats(filtered);
    setPage(1);
  }, [search, filterCategoria, filterEstado, allDocuments]);

  const loadData = async () => {
    const result = employeeId
      ? await loadEmployeeDocuments(employeeId)
      : await loadDocuments();
    if (result.success) {
      const docs = result.documents || [];
      setAllDocuments(docs);
      setDocuments(docs);
      calcStats(docs);
    }
  };

  const loadCategoriasData = async () => {
    const result = await loadCategorias();
    if (result.success) setCategorias(result.categorias || []);
  };

  const calcStats = (docs) => {
    setStats({
      total:    docs.length,
      approved: docs.filter(d => d.estado === 'approved').length,
      pending:  docs.filter(d => d.estado === 'pending').length,
      vencidos: docs.filter(d => d.dias_restantes !== null && d.dias_restantes < 0).length,
    });
  };

  const handleDownload = async (doc) => {
    await downloadDocument(doc.id, doc.archivo_nombre);
  };

  const handleUpdate = () => {
    loadData();
    setSelectedDocument(null);
  };

  const hasFilters  = search || filterCategoria || filterEstado;
  const totalPages  = Math.ceil(documents.length / PAGE_SIZE);
  const pagedDocs   = documents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-4">

      {/* ── Stat mini-cards ──────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total',      value: stats.total,    icon: <FileText     size={14} className="text-gray-400"   />, num: 'text-gray-900'  },
          { label: 'Aprobados',  value: stats.approved, icon: <CheckCircle  size={14} className="text-green-500"  />, num: 'text-green-700' },
          { label: 'Pendientes', value: stats.pending,  icon: <Clock        size={14} className="text-amber-500"  />, num: 'text-amber-700' },
          { label: 'Vencidos',   value: stats.vencidos, icon: <AlertTriangle size={14} className="text-red-500"   />, num: 'text-red-700'   },
        ].map(c => (
          <div key={c.label} className="bg-gray-50 rounded-xl border border-gray-200 p-3 flex items-center gap-3">
            {c.icon}
            <div>
              <p className={`text-xl font-bold ${c.num}`}>{c.value}</p>
              <p className="text-xs text-gray-400">{c.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filtros ───────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Búsqueda */}
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar documento, empleado..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>

        {/* Categoría */}
        <div className="relative">
          <select
            value={filterCategoria}
            onChange={e => setFilterCategoria(e.target.value)}
            className="appearance-none pl-3 pr-7 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          >
            <option value="">Todas las categorías</option>
            {categorias.map(c => (
              <option key={c.id} value={c.codigo}>{c.nombre}</option>
            ))}
          </select>
          <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Estado */}
        <div className="relative">
          <select
            value={filterEstado}
            onChange={e => setFilterEstado(e.target.value)}
            className="appearance-none pl-3 pr-7 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="pending">Pendiente</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
            <option value="expired">Vencido</option>
          </select>
          <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* ── Tabla ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw size={18} className="animate-spin mr-2" />
          <span className="text-sm">Cargando documentos...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FileText size={36} className="text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-500">
            {hasFilters ? 'Sin resultados para los filtros seleccionados' : 'No hay documentos cargados'}
          </p>
          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setFilterCategoria(''); setFilterEstado(''); }}
              className="mt-2 text-xs text-blue-600 hover:underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-800 text-white text-xs">
                <th className="px-4 py-2.5 text-left font-semibold">Documento</th>
                {!employeeId && <th className="px-4 py-2.5 text-left font-semibold">Empleado</th>}
                <th className="px-4 py-2.5 text-left font-semibold">Categoría</th>
                <th className="px-4 py-2.5 text-left font-semibold">Estado</th>
                <th className="px-4 py-2.5 text-left font-semibold">Vencimiento</th>
                <th className="px-4 py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pagedDocs.map((doc, idx) => (
                <tr
                  key={doc.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  {/* Documento */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <CategoryIcon categoria={doc.categoria_codigo} size="sm" />
                      <div>
                        <p className="font-semibold text-gray-900 text-xs leading-tight">
                          {doc.documento_nombre || doc.nombre}
                        </p>
                        <p className="text-xs text-gray-400">{doc.tipo_documento_nombre}</p>
                      </div>
                    </div>
                  </td>

                  {/* Empleado */}
                  {!employeeId && (
                    <td className="px-4 py-2.5">
                      <p className="text-xs text-gray-800">{doc.empleado_nombre}</p>
                      <p className="text-xs text-gray-400">{doc.empleado_codigo}</p>
                    </td>
                  )}

                  {/* Categoría */}
                  <td className="px-4 py-2.5">
                    <p className="text-xs text-gray-600">{doc.categoria_nombre}</p>
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-2.5">
                    <DocumentStatusBadge estado={doc.estado} size="sm" />
                  </td>

                  {/* Vencimiento */}
                  <td className="px-4 py-2.5">
                    {doc.fecha_vencimiento ? (
                      <>
                        <p className="text-xs text-gray-800">{fmtDate(doc.fecha_vencimiento)}</p>
                        {doc.dias_restantes !== null && (
                          <p className={`text-xs ${
                            doc.dias_restantes < 0  ? 'text-red-600'    :
                            doc.dias_restantes <= 7 ? 'text-orange-600' :
                            doc.dias_restantes <= 30? 'text-amber-600'  : 'text-gray-400'
                          }`}>
                            {doc.dias_restantes < 0
                              ? `Vencido hace ${Math.abs(doc.dias_restantes)}d`
                              : `Vence en ${doc.dias_restantes}d`}
                          </p>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setSelectedDocument(doc)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye size={14} />
                      </button>
                      {doc.archivo_url && (
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Descargar"
                        >
                          <Download size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={documents.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Modal detalles */}
      {selectedDocument && (
        <DocumentDetails
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Modal preview */}
      {previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}
